import React, { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Menu,
  X,
  Users,
  Building2,
  GitBranch,
  LogOut,
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  UserCog,
  Key,
  MessageSquare,
  Merge,
  Landmark,
  FileSpreadsheet,
} from 'lucide-react';
import ChangePasswordModal from './ChangePasswordModal';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Get user role name (lowercase for comparison)
  const userRole = user?.advisor?.role?.name?.toLowerCase() || '';
  const isAdmin = userRole === 'admin';

  // Define navigation with role-based access
  const allNavigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['admin', 'finance', 'workflow'] },
    { name: 'Clients', href: '/customers', icon: Users, roles: ['admin'] },
    { name: 'Caisses de pension', href: '/pension-funds', icon: Building2, roles: ['admin', 'finance'] },
    { name: 'Workflow', href: '/workflow', icon: GitBranch, roles: ['admin', 'workflow'] },
    { name: 'Interventions', href: '/interventions', icon: MessageSquare, roles: ['admin', 'workflow'] },
    { name: 'Utilisateurs', href: '/users', icon: UserCog, roles: ['admin'] },
    { name: 'Caisses', href: '/caisses', icon: Landmark, roles: ['admin'] },
    { name: 'Exports', href: '/exports', icon: FileSpreadsheet, roles: ['admin', 'finance'] },
    { name: 'Fusion PDF', href: '/tools/pdf-merger', icon: Merge, roles: ['admin', 'finance', 'workflow'] },
  ];

  // Filter navigation based on user role
  // If no role loaded yet, show all (will be restricted by route protection)
  const navigation = !userRole
    ? allNavigation
    : allNavigation.filter(item => item.roles.includes(userRole) || isAdmin);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const sidebarWidth = sidebarCollapsed ? 'w-[72px]' : 'w-64';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 ${sidebarWidth} bg-white border-r border-gray-200 transform transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 flex flex-col`}
      >
        {/* Logo area */}
        <div className={`flex h-16 items-center ${sidebarCollapsed ? 'justify-center px-3' : 'justify-between px-5'} border-b border-gray-100`}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
                <span className="text-white font-bold text-sm">LP</span>
              </div>
              <span className="text-lg font-semibold text-gray-900">LPP Backoffice</span>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">LP</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto py-4 ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
          <div className="space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  title={sidebarCollapsed ? item.name : undefined}
                  className={`group flex items-center ${sidebarCollapsed ? 'justify-center' : ''} px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                    active
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${sidebarCollapsed ? '' : 'mr-3'} ${active ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  {!sidebarCollapsed && (
                    <span>{item.name}</span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Collapse toggle (desktop only) */}
        <div className="hidden lg:block px-3 py-2 border-t border-gray-100">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center w-full' : ''} px-3 py-2 rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors w-full`}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <>
                <ChevronLeft className="h-5 w-5 mr-3" />
                <span>Réduire le menu</span>
              </>
            )}
          </button>
        </div>

        {/* User section */}
        <div className={`border-t border-gray-100 ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
          <div className={`flex items-center ${sidebarCollapsed ? 'flex-col gap-2' : 'gap-3'} ${sidebarCollapsed ? '' : 'p-2 rounded-lg'}`}>
            <div className="flex-shrink-0">
              <div className={`${sidebarCollapsed ? 'w-9 h-9' : 'w-9 h-9'} rounded-full bg-primary-100 flex items-center justify-center`}>
                <span className="text-primary-700 font-semibold text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
            {!sidebarCollapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setShowChangePassword(true)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    title="Changer le mot de passe"
                  >
                    <Key className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleLogout}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Déconnexion"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </>
            )}
            {sidebarCollapsed && (
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Changer le mot de passe"
                >
                  <Key className="h-4 w-4" />
                </button>
                <button
                  onClick={handleLogout}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Déconnexion"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-64'}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-14 bg-white border-b border-gray-200 flex items-center px-4 lg:px-6">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 -ml-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
};

export default Layout;
